import {
  Component, inject, input, output, model, ChangeDetectionStrategy
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { TranslocoModule } from '@jsverse/transloco';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { RelationTableDialogComponent } from './relation-table-dialog.component';
import { RelationTableColumn, RelationTableDialogData, RelationTableDialogResult } from './relation-table.types';

@Component({
  selector: 'pixl-relation-table-select',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatChipsModule, TranslocoModule, CdkDropList, CdkDrag],
  templateUrl: './relation-table-select.component.html',
  styleUrl: './relation-table-select.component.scss'
})
export class RelationTableSelectComponent<T extends object> {
  private dialog = inject(MatDialog);

  label = input.required<string>();
  dialogTitle = input.required<string>();
  columns = input.required<RelationTableColumn[]>();
  displayColumn = input.required<string>();
  idKey = input<string>('id');
  multiple = input<boolean>(true);
  searchLabel = input<string | undefined>(undefined);
  reorderable = input<boolean>(false);

  loadFn = input.required<(params: { page: number, pageSize: number, search: string }) => Promise<{ data: T[], total: number }>>();

  selectedItems = model<T[]>([]);
  selectionChange = output<T[]>();

  dialogWidth = input<string>('800px');

  openDialog(): void {
    const dialogData: RelationTableDialogData<T> = {
      title: this.dialogTitle(),
      columns: this.columns(),
      loadFn: this.loadFn(),
      selectedItems: this.selectedItems(),
      idKey: this.idKey(),
      multiple: this.multiple(),
      searchLabel: this.searchLabel()
    };

    const dialogRef = this.dialog.open(RelationTableDialogComponent, {
      width: this.dialogWidth(),
      maxWidth: this.dialogWidth(),
      maxHeight: '80vh',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe((result: RelationTableDialogResult<T> | null) => {
      if (result) {
        this.selectedItems.set(result.selectedItems);
        this.selectionChange.emit(result.selectedItems);
      }
    });
  }

  removeItem(item: T): void {
    const idKey = this.idKey();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updated = this.selectedItems().filter(s => (s as any)[idKey] !== (item as any)[idKey]);
    this.selectedItems.set(updated);
    this.selectionChange.emit(updated);
  }

  reorder(event: CdkDragDrop<T[]>): void {
    const items = [...this.selectedItems()];
    moveItemInArray(items, event.previousIndex, event.currentIndex);
    this.selectedItems.set(items);
    this.selectionChange.emit(items);
  }
}
